<template>
  <BaseModal
    :is-open="isOpen"
    data-testid="log-exercise-modal"
    @close="handleClose"
    @dismiss="handleClose"
    :title="modalTitle"
    size="md"
  >
    <BaseSelect
      v-model="selectedExerciseId"
      :options="exerciseOptions"
      label="Exercise type"
      placeholder="Select an exercise"
    />

    <!-- System Exercise Flow -->
    <div v-if="isSystemExercise" class="exercise-content">
      <div class="selected-exercise-header">
        <BaseIcon v-if="selectedExerciseType?.icon" :icon="selectedExerciseType?.icon" size="lg" class="exercise-icon" />
        <h3 class="selected-exercise-name">{{ selectedExerciseType?.name }}</h3>
      </div>

      <!-- Intensity Selection -->
      <div class="intensity-selection">
        <label class="input-label">Intensity</label>
        <div class="intensity-buttons">
          <BaseButton
            v-for="level in intensityLevels"
            :key="level"
            :variant="intensity === level ? 'primary' : 'secondary'"
            size="md"
            @click="intensity = level"
            class="intensity-button"
          >
            {{ level.charAt(0).toUpperCase() + level.slice(1) }}
          </BaseButton>
        </div>
      </div>

      <BaseInput
        v-model="startTimeInput"
        type="time"
        label="Start time"
        :error="startTimeError"
      />

      <!-- Duration Input -->
      <BaseInput
        v-model="durationInput"
        type="number"
        label="Duration (minutes)"
        placeholder="30"
        inputmode="numeric"
        :error="durationError"
      />

      <!-- Calculated Calories Display -->
      <div v-if="calculatedCalories !== null" class="calories-display">
        <div class="calories-label">Calories Burned</div>
        <div class="calories-value">{{ calculatedCalories }} kcal</div>
      </div>

      <!-- Confirm Button -->
      <BaseButton
        variant="primary"
        size="lg"
        fullWidth
        :disabled="!canConfirmSystem"
        @click="confirmSystemExercise"
      >
        {{ confirmLabel }}
      </BaseButton>
    </div>

    <!-- Custom Exercise Flow -->
    <div v-else-if="isCustomExercise" class="exercise-content">
      <BaseInput
        v-model="customName"
        type="text"
        label="Exercise Name"
        placeholder="e.g., Rock Climbing"
        :error="customNameError"
      />

      <BaseInput
        v-model="startTimeInput"
        type="time"
        label="Start time"
        :error="startTimeError"
      />

      <BaseInput
        v-model="customDurationInput"
        type="number"
        label="Duration (minutes)"
        placeholder="45"
        inputmode="numeric"
        :error="customDurationError"
      />

      <BaseInput
        v-model="customCaloriesInput"
        type="number"
        label="Calories Burned"
        placeholder="350"
        inputmode="numeric"
        :error="customCaloriesError"
      />

      <BaseButton
        variant="primary"
        size="lg"
        fullWidth
        :disabled="!canConfirmCustom"
        @click="confirmCustomExercise"
      >
        {{ confirmLabel }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { generateId } from "@/utils/id";
import { ref, computed, watch } from 'vue';
import BaseModal from '@/components/ui/BaseModal.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BaseSelect, { type SelectOption } from '@/components/ui/BaseSelect.vue';
import {
  SYSTEM_EXERCISE_TYPES,
  calculateExerciseCalories,
  type ExerciseType,
  type ExerciseIntensity,
} from '@/stores/profileStore';
import { useWeightStore } from '@/stores/weightStore';
import { useExercisePreferencesStore } from '@/stores/exercisePreferencesStore';
import type { MealId } from '@/stores/mealsStore';
import type { DiaryEntry } from '@/stores/diaryStore';
import { buildLoggedAt, formatTimeInput, toLocalDateInput, toLocalTimeInput } from '@/utils/dateTime';

const props = defineProps<{
  isOpen: boolean;
  mealId: MealId | null;
  mealPlanId?: string | null;
  selectedDate: string;
  initialEntry?: DiaryEntry | null;
}>();

const emit = defineEmits<{
  close: [];
  save: [entry: DiaryEntry];
}>();

// System Exercise State
const selectedExerciseId = ref<string | null>(null);
const DEFAULT_DURATION_MINUTES = '30';
const intensity = ref<ExerciseIntensity>('medium');
const intensityLevels: ExerciseIntensity[] = ['low', 'medium', 'high'];
const durationInput = ref<string>('');
const durationError = ref<string>('');

// Start time (HH:MM, local), pre-filled with the current time on open, or with
// the entry's own time when editing. `initialStartTime` remembers the edit
// prefill so an untouched time keeps the entry's exact timestamp.
const startTimeInput = ref<string>(formatTimeInput());
const initialStartTime = ref<string>('');
const START_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const hasValidStartTime = computed(() => START_TIME_PATTERN.test(startTimeInput.value));
const startTimeError = computed(() =>
  hasValidStartTime.value ? '' : 'Enter a start time',
);

// Custom Exercise State
const customName = ref<string>('');
const customDurationInput = ref<string>('');
const customCaloriesInput = ref<string>('');
const customNameError = ref<string>('');
const customDurationError = ref<string>('');
const customCaloriesError = ref<string>('');

// Get user weight for MET calculation from the weight log (latest known).
const userWeight = ref(70);
const weightStore = useWeightStore();
const exercisePreferencesStore = useExercisePreferencesStore();

const loadUserWeight = async () => {
  try {
    await weightStore.ensureLoaded();
    const latest = weightStore.getWeightForDay();
    if (latest && Number.isFinite(latest.weightKg) && latest.weightKg > 0) {
      userWeight.value = latest.weightKg;
    }
  } catch {
    userWeight.value = 70;
  }
};

const exerciseOptions = computed<SelectOption[]>(() => [
  ...SYSTEM_EXERCISE_TYPES.map((type) => ({
    value: type.id,
    label: type.name,
  })),
  { value: 'custom', label: 'Custom exercise' },
]);

const selectedExerciseType = computed<ExerciseType | null>(() => {
  if (!selectedExerciseId.value || selectedExerciseId.value === 'custom') {
    return null;
  }
  return SYSTEM_EXERCISE_TYPES.find((type) => type.id === selectedExerciseId.value) ?? null;
});

const isCustomExercise = computed(() => selectedExerciseId.value === 'custom');
const isSystemExercise = computed(() => !!selectedExerciseType.value);
const isEditing = computed(() => props.initialEntry != null);
const modalTitle = computed(() => (isEditing.value ? 'Edit Exercise' : 'Log Exercise'));
const confirmLabel = computed(() => (isEditing.value ? 'Save changes' : 'Log Exercise'));

// Calculated calories for system exercise
const calculatedCalories = computed(() => {
  if (!selectedExerciseType.value || !durationInput.value) {
    return null;
  }

  const duration = Number(durationInput.value);
  if (!Number.isFinite(duration) || duration <= 0) {
    return null;
  }

  const met = selectedExerciseType.value.metValues[intensity.value];
  return calculateExerciseCalories(met, userWeight.value, duration);
});

// Validation
const canConfirmSystem = computed(() => {
  return (
    hasValidStartTime.value &&
    selectedExerciseType.value !== null &&
    calculatedCalories.value !== null &&
    calculatedCalories.value > 0
  );
});

const canConfirmCustom = computed(() => {
  const name = customName.value.trim();
  const duration = Number(customDurationInput.value);
  const calories = Number(customCaloriesInput.value);

  return (
    hasValidStartTime.value &&
    name.length > 0 &&
    Number.isFinite(duration) &&
    duration > 0 &&
    Number.isFinite(calories) &&
    calories > 0
  );
});

// An edited entry keeps its exact timestamp unless the time was changed — its
// loggedAt is part of the intervals.icu workout de-duplication fingerprint.
const resolveLoggedAt = (): string => {
  const existing = props.initialEntry;
  if (existing && startTimeInput.value === initialStartTime.value) {
    return existing.loggedAt;
  }
  const date = existing
    ? toLocalDateInput(existing.loggedAt) || props.selectedDate
    : props.selectedDate;
  return buildLoggedAt(date, startTimeInput.value);
};

// Actions
const confirmSystemExercise = () => {
  if (!canConfirmSystem.value || !selectedExerciseType.value) {
    return;
  }

  const existingEntry = props.initialEntry;
  const duration = Number(durationInput.value);
  const calories = calculatedCalories.value!;
  const exerciseName = `${selectedExerciseType.value.name} (${intensity.value.charAt(0).toUpperCase() + intensity.value.slice(1)})`;

  const entry: DiaryEntry = {
    id: existingEntry?.id ?? generateId(),
    loggedAt: resolveLoggedAt(),
    mealId: props.mealId,
    mealPlanId: props.mealPlanId ?? existingEntry?.mealPlanId ?? null,
    serving: {
      amount: duration,
      unit: 'minutes',
    },
    nutritionSnapshot: {
      calories,
      protein: 0,
      carbs: 0,
      fat: 0,
    },
    source: {
      type: 'exercise',
      label: exerciseName,
      exerciseTypeId: selectedExerciseType.value.id,
    },
    exercise: {
      durationMinutes: duration,
      intensity: intensity.value,
    },
    createdAt: existingEntry?.createdAt ?? new Date().toISOString(),
  };

  emit('save', entry);
  resetForm();
  emit('close');
};

const confirmCustomExercise = () => {
  if (!canConfirmCustom.value) {
    return;
  }

  const existingEntry = props.initialEntry;
  const name = customName.value.trim();
  const duration = Number(customDurationInput.value);
  const calories = Number(customCaloriesInput.value);

  const entry: DiaryEntry = {
    id: existingEntry?.id ?? generateId(),
    loggedAt: resolveLoggedAt(),
    mealId: props.mealId,
    mealPlanId: props.mealPlanId ?? existingEntry?.mealPlanId ?? null,
    serving: {
      amount: duration,
      unit: 'minutes',
    },
    nutritionSnapshot: {
      calories,
      protein: 0,
      carbs: 0,
      fat: 0,
    },
    source: {
      type: 'exercise',
      label: name,
    },
    exercise: {
      durationMinutes: duration,
      intensity: 'medium', // default for custom exercises
    },
    createdAt: existingEntry?.createdAt ?? new Date().toISOString(),
  };

  emit('save', entry);
  resetForm();
  emit('close');
};

const handleClose = () => {
  resetForm(!isEditing.value);
  emit('close');
};

const populateFromEntry = (entry: DiaryEntry) => {
  startTimeInput.value = toLocalTimeInput(entry.loggedAt) || formatTimeInput();
  initialStartTime.value = startTimeInput.value;
  if (entry.source.exerciseTypeId) {
    selectedExerciseId.value = entry.source.exerciseTypeId;
    intensity.value = entry.exercise?.intensity ?? 'medium';
    durationInput.value = String(entry.exercise?.durationMinutes ?? entry.serving.amount);
    durationError.value = '';
    customName.value = '';
    customDurationInput.value = DEFAULT_DURATION_MINUTES;
    customCaloriesInput.value = '';
    customNameError.value = '';
    customDurationError.value = '';
    customCaloriesError.value = '';
    return;
  }

  selectedExerciseId.value = 'custom';
  customName.value = entry.source.label ?? '';
  customDurationInput.value = String(entry.exercise?.durationMinutes ?? entry.serving.amount);
  customCaloriesInput.value = String(entry.nutritionSnapshot.calories);
  customNameError.value = '';
  customDurationError.value = '';
  customCaloriesError.value = '';
  intensity.value = 'medium';
  durationInput.value = DEFAULT_DURATION_MINUTES;
  durationError.value = '';
};

const resetForm = (preserveSelection = true) => {
  if (!preserveSelection) {
    selectedExerciseId.value = null;
  }
  intensity.value = 'medium';
  durationInput.value = DEFAULT_DURATION_MINUTES;
  durationError.value = '';
  customName.value = '';
  customDurationInput.value = DEFAULT_DURATION_MINUTES;
  customCaloriesInput.value = '';
  customNameError.value = '';
  customDurationError.value = '';
  customCaloriesError.value = '';
};

// Validate duration input
watch(durationInput, (value) => {
  const duration = Number(value);
  if (value && (!Number.isFinite(duration) || duration <= 0)) {
    durationError.value = 'Duration must be a positive number';
  } else {
    durationError.value = '';
  }
});

watch(customDurationInput, (value) => {
  const duration = Number(value);
  if (value && (!Number.isFinite(duration) || duration <= 0)) {
    customDurationError.value = 'Duration must be a positive number';
  } else {
    customDurationError.value = '';
  }
});

watch(customCaloriesInput, (value) => {
  const calories = Number(value);
  if (value && (!Number.isFinite(calories) || calories <= 0)) {
    customCaloriesError.value = 'Calories must be a positive number';
  } else {
    customCaloriesError.value = '';
  }
});

watch(customName, (value) => {
  if (value && value.trim().length === 0) {
    customNameError.value = 'Exercise name is required';
  } else {
    customNameError.value = '';
  }
});

watch(selectedExerciseId, (value) => {
  if (value) {
    exercisePreferencesStore.setLastExerciseType(value);
  }

  if (value === 'custom') {
    intensity.value = 'medium';
    durationInput.value = DEFAULT_DURATION_MINUTES;
    durationError.value = '';
  } else {
    customName.value = '';
    customDurationInput.value = DEFAULT_DURATION_MINUTES;
    customCaloriesInput.value = '';
    customNameError.value = '';
    customDurationError.value = '';
    customCaloriesError.value = '';
  }
});

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      loadUserWeight();
      if (props.initialEntry) {
        populateFromEntry(props.initialEntry);
        return;
      }

      startTimeInput.value = formatTimeInput();
      initialStartTime.value = '';

      const lastSelected = exercisePreferencesStore.isLoaded
        ? exercisePreferencesStore.lastExerciseTypeId
        : exercisePreferencesStore.load();
      if (!selectedExerciseId.value && lastSelected) {
        selectedExerciseId.value = lastSelected;
      }
      if (!durationInput.value) {
        durationInput.value = DEFAULT_DURATION_MINUTES;
      }
      if (!customDurationInput.value) {
        customDurationInput.value = DEFAULT_DURATION_MINUTES;
      }
    }
  },
);

watch(
  () => props.initialEntry,
  (entry) => {
    if (!props.isOpen || !entry) {
      return;
    }
    populateFromEntry(entry);
  },
);
</script>

<style scoped>
.exercise-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.exercise-icon {
  color: hsl(var(--color-primary));
}

.selected-exercise-header {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md);
  background: hsl(var(--color-background-secondary));
  border-radius: var(--radius-md);
}

.selected-exercise-name {
  flex: 1;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: hsl(var(--color-text-primary));
}

.intensity-selection {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.input-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: hsl(var(--color-text-secondary));
}

.intensity-buttons {
  display: flex;
  gap: var(--space-sm);
}

.intensity-button {
  flex: 1;
}

.calories-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-lg);
  background: hsl(var(--color-background-secondary));
  border-radius: var(--radius-md);
  border: 2px solid hsl(var(--color-primary));
}

.calories-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: hsl(var(--color-text-secondary));
  margin-bottom: var(--space-xs);
}

.calories-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: hsl(var(--color-primary));
}
</style>
